import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import { Mail, Send, BellRing, AlertTriangle } from "lucide-react";
import { API_URL, formatCurrency, formatDate } from "../../lib/utils";
import { toastApiError } from "../../lib/apiErrors";

export const EmailSettingsCard = ({ settings, setSettings }) => {
  const [status, setStatus] = useState(null);
  const [overdue, setOverdue] = useState(null);
  const [log, setLog] = useState([]);
  const [busy, setBusy] = useState("");
  const opts = { withCredentials: true };

  const loadOverdue = (days) => axios.get(`${API_URL}/customers/overdue${days != null ? `?days=${days}` : ""}`, opts).then((r) => setOverdue(r.data)).catch(() => {});
  useEffect(() => {
    axios.get(`${API_URL}/email/status`, opts).then((r) => setStatus(r.data)).catch(() => {});
    axios.get(`${API_URL}/customers/debt-reminders/log?limit=10`, opts).then((r) => setLog(r.data)).catch(() => {});
    loadOverdue(settings.debt_reminder_days);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendTest = async () => {
    setBusy("test");
    try { const r = await axios.post(`${API_URL}/email/test`, {}, opts); toast.success(r.data.message); }
    catch (e) { toastApiError(e, "Erro no envio de teste"); }
    finally { setBusy(""); }
  };

  const sendReminders = async () => {
    setBusy("rem");
    try {
      const r = await axios.post(`${API_URL}/customers/debt-reminders/send`, {}, opts);
      const d = r.data;
      (d.sent ? toast.success : toast.info)(`${d.sent} lembrete(s) enviado(s) · ${d.skipped_no_email} sem email · ${d.skipped_recent} já avisados recentemente${d.failed ? ` · ${d.failed} falhado(s)` : ""}`);
      axios.get(`${API_URL}/customers/debt-reminders/log?limit=10`, opts).then((x) => setLog(x.data)).catch(() => {});
    } catch (e) { toastApiError(e, "Erro ao enviar lembretes"); }
    finally { setBusy(""); }
  };

  const set = (k, v) => setSettings({ ...settings, [k]: v });
  const n = overdue?.customers?.length || 0;
  const noEmail = (overdue?.customers || []).filter((c) => !c.email).length;

  return (
    <Card data-testid="email-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Mail className="w-5 h-5" /> Email aos clientes</CardTitle>
        <CardDescription>Facturas, recibos e extratos enviados por email — remetente, respostas e lembretes automáticos de dívida</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-lg border p-3 text-sm flex flex-wrap items-center gap-3" data-testid="email-status-block">
          {status ? (
            <>
              <Badge className={status.enabled === false ? "bg-red-500/15 text-red-700" : status.configured ? "bg-emerald-500/15 text-emerald-700" : "bg-red-500/15 text-red-700"} data-testid="email-status-badge">
                {status.enabled === false ? "Desactivado pelo dono da plataforma" : status.configured ? "Envio activo" : "Não configurado"}
              </Badge>
              <span>Remetente automático: <strong>{settings.email_sender_name || status.sender_name}</strong> &lt;{status.from_email || "—"}&gt;{status.reply_to ? <> · respostas para <strong>{status.reply_to}</strong></> : null}</span>
              {status.enabled === false && <span className="w-full text-xs text-red-700" data-testid="email-disabled-help">O dono da plataforma desligou o envio de email aos clientes para esta empresa. Peça a reactivação no Backoffice › Email.</span>}
              {status.test_mode && <span className="flex items-center gap-1 text-amber-700" data-testid="email-test-mode-warning"><AlertTriangle className="w-4 h-4" /> Remetente de teste — só entrega ao email da conta Resend. Peça ao dono da plataforma para definir o remetente em Backoffice › Email.</span>}
              {status.configured && status.enabled !== false && !status.reply_to && (
                <span className="w-full text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 flex items-center gap-1" data-testid="email-reply-to-missing">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" /> Preencha o email da empresa (Dados da Empresa) ou o reply-to abaixo para receber as respostas dos clientes.
                </span>
              )}
              {!status.configured && (
                <span className="w-full text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs" data-testid="email-not-configured-help">
                  O envio de email ainda não foi activado pelo dono da plataforma (Backoffice › Email). Até lá os botões de email ficam inactivos — não precisa de configurar nada do seu lado.
                </span>
              )}
              <Button size="sm" variant="outline" className="ml-auto" disabled={busy === "test" || !status.configured || status.enabled === false} onClick={sendTest} data-testid="email-send-test-btn"><Send className="w-3.5 h-3.5 mr-1" /> {busy === "test" ? "A enviar…" : `Enviar teste para ${status.your_email}`}</Button>
            </>
          ) : <span className="text-muted-foreground">A verificar…</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Nome do remetente</Label>
            <Input value={settings.email_sender_name || ""} onChange={(e) => set("email_sender_name", e.target.value)} placeholder={settings.restaurant_name || "Nome da empresa"} data-testid="email-sender-name-input" />
            <p className="text-xs text-muted-foreground">Aparece como "Nome &lt;{status?.from_email || "remetente"}&gt;" na caixa de entrada do cliente</p>
          </div>
          <div className="space-y-2">
            <Label>Email para receber respostas (reply-to)</Label>
            <Input type="email" value={settings.email_reply_to || ""} onChange={(e) => set("email_reply_to", e.target.value)} placeholder={settings.email || "geral@suaempresa.ao"} data-testid="email-reply-to-input" />
            <p className="text-xs text-muted-foreground">Quando o cliente responde à factura ou ao lembrete, a resposta chega a este email</p>
          </div>
        </div>

        <div className="rounded-lg border p-4 space-y-4" data-testid="debt-reminder-block">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-semibold flex items-center gap-2"><BellRing className="w-4 h-4" /> Lembrete automático de dívidas</p>
              <p className="text-xs text-muted-foreground mt-1">Todos os dias às 09h envia o extrato (PDF) aos clientes com facturas vencidas há mais de N dias, sem repetir ao mesmo cliente antes do intervalo definido.</p>
            </div>
            <Switch checked={!!settings.debt_reminder_enabled} onCheckedChange={(v) => set("debt_reminder_enabled", v)} className="data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-600" data-testid="debt-reminder-switch" />
          </div>
          <div className="grid grid-cols-2 gap-4 max-w-md">
            <div className="space-y-1">
              <Label>Vencidas há mais de (dias)</Label>
              <Input type="number" min="0" value={settings.debt_reminder_days ?? 15} onChange={(e) => { set("debt_reminder_days", Number(e.target.value)); }} onBlur={(e) => loadOverdue(Number(e.target.value))} data-testid="debt-reminder-days-input" />
            </div>
            <div className="space-y-1">
              <Label>Intervalo entre lembretes (dias)</Label>
              <Input type="number" min="1" value={settings.debt_reminder_interval_days ?? 7} onChange={(e) => set("debt_reminder_interval_days", Number(e.target.value))} data-testid="debt-reminder-interval-input" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-sm">
            <span data-testid="debt-reminder-preview">{overdue ? <><strong>{n}</strong> cliente(s) com facturas vencidas há mais de {overdue.days} dias{noEmail ? <span className="text-amber-700"> · {noEmail} sem email na ficha</span> : null}</> : "…"}</span>
            <Button size="sm" variant="outline" className="ml-auto" disabled={busy === "rem" || !n} onClick={sendReminders} data-testid="debt-reminder-send-now-btn"><Send className="w-3.5 h-3.5 mr-1" /> {busy === "rem" ? "A enviar…" : "Enviar lembretes agora"}</Button>
          </div>
          {overdue?.customers?.length > 0 && (
            <ul className="text-xs space-y-1 max-h-32 overflow-y-auto pr-20" data-testid="debt-reminder-candidates">
              {overdue.customers.slice(0, 8).map((c) => (
                <li key={c.customer_id} className="flex justify-between gap-2">
                  <span>{c.name} <span className="text-muted-foreground">{c.email || "— sem email"}</span></span>
                  <span className="font-mono">{formatCurrency(c.overdue_total)} · {c.oldest_days}d</span>
                </li>
              ))}
            </ul>
          )}
          {log.length > 0 && (
            <div className="text-xs" data-testid="debt-reminder-log">
              <p className="font-semibold uppercase text-muted-foreground mb-1">Últimos lembretes enviados</p>
              <ul className="space-y-0.5">
                {log.map((l) => <li key={l.reminder_id}>{formatDate(l.created_at)} · {l.customer_name} → {l.to} · {formatCurrency(l.overdue_total)}</li>)}
              </ul>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground">Guarde as definições (botão no topo) para aplicar o nome do remetente, o reply-to e o lembrete automático.</p>
      </CardContent>
    </Card>
  );
};
